import type { DataFreshness } from '../api/client.js';

export type ProtectedOperation = NonNullable<
  DataFreshness['protectedChanges']
>['largestOperations'][number];

// How many protected rows ONE operation must remove or change before the
// footer raises a badge.
//
// The badge has to stay silent on routine days or it becomes wallpaper, and a
// badge nobody reads is exactly the failure it exists to fix. Daily ingest
// legitimately moves protected rows in small numbers: a CQC refresh re-points
// a handful of crosswalks, a re-geocode tightens a few coordinates, a user
// deletes a watchlist. Those land in the tens per operation. The losses that
// went unnoticed were a different order of magnitude — 18,067 scores in one
// crash, a whole attribution load cleared at once, CZ's ownership graph
// replaced wholesale. 500 sits well clear of the first and far below the
// second, so a real bulk clear always trips it and a normal night never does.
export const LARGE_PROTECTED_OPERATION_ROWS = 500;

// An operation deserves the badge when it is large and NOBODY HAS RESTORED ANY
// OF IT. "Any", not "all", deliberately: restore_protected() is fill-only by
// default and stamps only the captures it actually wrote back, so a correct
// restore after the loader re-created some rows still leaves restoredRows <
// rows forever. Requiring a full restore would keep the badge lit for a week
// after the problem was handled. The badge's job is to get the operation
// looked at; once someone has run a restore on it, it has been.
export function isLargeUnrestoredOperation(op: ProtectedOperation): boolean {
  return op.rows >= LARGE_PROTECTED_OPERATION_ROWS && op.restoredRows === 0;
}

// Reads the API's top-10 list. An unrestored ≥500-row operation can only be
// missing from it if ten LARGER operations happened the same week — in which
// case the badge is already lit and the tooltip already lists the worst.
export function largeUnrestoredOperations(data: DataFreshness): ProtectedOperation[] {
  return (data.protectedChanges?.largestOperations ?? []).filter(isLargeUnrestoredOperation);
}

const OP_WORD: Record<string, string> = { D: 'deleted', U: 'changed', T: 'truncated' };

export function describeOps(ops: string): string {
  return ops
    .split('')
    .map((o) => OP_WORD[o] ?? o)
    .join('/');
}
